import { cn } from "@/lib/utils";
import type { ConversationSummary } from "@/lib/types";
import { Button } from "@/components/ui/button";
import { Compass, Plus, Sparkles, LogOut, X } from "lucide-react";
import { Link, useLocation, useNavigate } from "react-router";
import type { User } from "@supabase/supabase-js";

type Props = {
  conversations: ConversationSummary[];
  user: User | null;
  onSignOut: () => void;
  open: boolean;
  onClose: () => void;
};

export function Sidebar({
  conversations,
  user,
  onSignOut,
  open,
  onClose,
}: Props) {
  const navigate = useNavigate();
  const location = useLocation();

  const initials =
    (user?.user_metadata?.full_name as string | undefined)
      ?.split(" ")
      .map((s) => s[0])
      .slice(0, 2)
      .join("")
      .toUpperCase() ??
    user?.email?.slice(0, 2).toUpperCase() ??
    "??";

  return (
    <aside
      className={cn(
        "flex h-screen w-72 shrink-0 flex-col border-r border-border/60 bg-sidebar",
        "fixed inset-y-0 left-0 z-40 transition-transform duration-200 ease-out",
        "md:static md:w-64 md:translate-x-0",
        open ? "translate-x-0" : "-translate-x-full md:translate-x-0",
      )}
    >
      <div className="flex items-center gap-2 px-5 pt-5 pb-4">
        <div className="grid size-8 place-items-center rounded-md bg-primary/15 ring-1 ring-primary/30">
          <Compass className="size-4 text-primary" strokeWidth={2.2} />
        </div>
        <span className="font-serif text-xl tracking-tight italic">
          sanplexity
        </span>
        <Button
          onClick={onClose}
          variant="ghost"
          size="icon-sm"
          aria-label="Close menu"
          className="ml-auto md:hidden"
        >
          <X className="size-4" />
        </Button>
      </div>

      <div className="px-3">
        <Button
          onClick={() => {
            navigate("/");
            onClose();
          }}
          variant="outline"
          className={cn(
            "h-11 w-full justify-start gap-2 rounded-lg border-border/60 bg-card/50",
            "hover:border-primary/40 hover:bg-card",
          )}
        >
          <Plus className="size-4 text-primary" strokeWidth={2.4} />
          <span className="text-sm font-medium">New thread</span>
          <kbd className="ml-auto hidden rounded border border-border bg-background/60 px-1.5 py-0.5 font-mono text-[10px] text-muted-foreground sm:inline">
            ⌘K
          </kbd>
        </Button>
      </div>

      <div className="mt-6 flex min-h-0 flex-1 flex-col">
        <div className="flex items-center justify-between px-5 pb-2">
          <span className="text-[11px] font-medium tracking-[0.14em] text-muted-foreground uppercase">
            Library
          </span>
          <Sparkles className="size-3 text-muted-foreground/60" />
        </div>

        <nav className="scrollbar-thin flex-1 overflow-y-auto px-2 pb-4">
          {conversations.length === 0 ? (
            <p className="px-3 py-2 text-xs text-muted-foreground/70">
              No threads yet. Start one above.
            </p>
          ) : (
            <ul className="space-y-0.5">
              {conversations.map((c) => {
                const active = location.pathname === `/c/${c.id}`;
                return (
                  <li key={c.id}>
                    <Link
                      to={`/c/${c.id}`}
                      onClick={onClose}
                      className={cn(
                        "block truncate rounded-md px-3 py-2 text-sm transition-colors",
                        active
                          ? "bg-secondary text-foreground"
                          : "text-muted-foreground hover:bg-secondary/50 hover:text-foreground",
                      )}
                    >
                      {c.title}
                    </Link>
                  </li>
                );
              })}
            </ul>
          )}
        </nav>
      </div>

      <div className="border-t border-border/60 p-3">
        <div className="flex items-center gap-3 rounded-lg p-2">
          <div className="grid size-8 place-items-center rounded-full bg-gradient-to-br from-primary/40 to-primary/10 text-xs font-semibold ring-1 ring-primary/30">
            {initials}
          </div>
          <div className="min-w-0 flex-1">
            <div className="truncate text-sm font-medium">
              {(user?.user_metadata?.full_name as string | undefined) ??
                user?.email ??
                "Guest"}
            </div>
            <div className="truncate text-[11px] text-muted-foreground">
              {user?.email}
            </div>
          </div>
          <Button
            onClick={onSignOut}
            variant="ghost"
            size="icon-sm"
            title="Sign out"
            className="text-muted-foreground hover:text-foreground"
          >
            <LogOut className="size-3.5" />
          </Button>
        </div>
      </div>
    </aside>
  );
}
